import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  ChevronDown,
  Facebook,
  Landmark,
  Mail,
  QrCode,
  Send,
  ShieldCheck,
  Users,
} from "lucide-react";
import { LanguageSwitch } from "./NavbarTerritorialeTop";
import ProtocolloContattoModal from "../protocollo/ProtocolloContattoModal";
import { subscribeNewsletter } from "../../api";
import { useLang } from "../../LangContext";

/* BLOCCO C: etichette via t("footx.*") — traduzioni in i18n.js (da rivedere madrelingua). */
const COLUMNS = [
  {
    id: "piazza",
    tKey: "footx.col_piazza",
    links: [
      { to: "/piazze", tKey: "footx.l_piazze" },
      { to: "/eventi", tKey: "footx.l_eventi" },
      { to: "/manifesto", tKey: "footx.l_manifesto" },
      { to: "/regole-piazza", tKey: "footx.l_regole" },
    ],
  },
  {
    id: "mappe",
    tKey: "footx.col_mappe",
    links: [
      { to: "/esplora-la-mappa", tKey: "footx.l_mappa3d" },
      { to: "/qr", tKey: "footx.l_arqr" },
      { to: "/mappa-del-mondo", tKey: "footx.l_mappamondo" },
      { to: "/qr", tKey: "footx.l_guideqr" },
    ],
  },
  {
    id: "mondo",
    tKey: "footx.col_mondo",
    links: [
      { to: "/gemellaggi", tKey: "footx.l_gemellaggi" },
      { to: "/comuni-amici", tKey: "footx.l_comuniamici" },
      { to: "/mondo", tKey: "footx.l_mondo" },
      { to: "/universita", tKey: "footx.l_universita" },
    ],
  },
  {
    id: "enti",
    tKey: "footx.col_enti",
    links: [
      { to: "/governance", tKey: "footx.l_governance" },
      { to: "/stanza-sindaci", tKey: "footx.l_sindaci" },
      { to: "/aderisci", tKey: "footx.l_aderisci" },
      { to: "/richiedi-accesso", tKey: "footx.l_accesso" },
      { to: "/ufficio-stampa", tKey: "footx.l_stampa" },
      { contact: true, tKey: "footx.l_ambasciatore" },
    ],
  },
];

const INFO_CARDS = [
  {
    to: "/qr",
    icon: QrCode,
    tTitle: "footx.card_ar_t",
    tText: "footx.card_ar_x",
    testid: "footer-card-ar",
  },
  {
    to: "/gemellaggi",
    icon: Users,
    tTitle: "footx.card_gem_t",
    tText: "footx.card_gem_x",
    testid: "footer-card-gemellaggi",
  },
  {
    to: "/governance",
    icon: Landmark,
    tTitle: "footx.card_ist_t",
    tText: "footx.card_ist_x",
    testid: "footer-card-istituzioni",
  },
];

const LEGAL = [
  { to: "/chi-siamo", tKey: "footx.leg_chisiamo" },
  { to: "/contatti", tKey: "footx.leg_contatti" },
  { to: "/privacy", tKey: "footx.leg_privacy" },
  { to: "/cookie-policy", tKey: "footx.leg_cookie" },
  { to: "/termini", tKey: "footx.leg_termini" },
  { to: "/dati-societari", tKey: "footx.leg_dati" },
];

function FooterColumn({ col, onContact, t }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-[#eedfcb] md:border-0">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls={`footer-col-${col.id}`}
        className="w-full min-h-[48px] flex items-center justify-between md:hidden text-left font-sans font-bold text-stone-950"
      >
        {t(col.tKey)}
        <ChevronDown size={18} className={`transition-transform ${open ? "rotate-180" : ""}`} aria-hidden />
      </button>
      <h5 className="hidden md:block font-sans font-bold text-stone-950 mb-3 text-sm uppercase tracking-widest">
        {t(col.tKey)}
      </h5>
      <ul
        id={`footer-col-${col.id}`}
        className={`${open ? "block" : "hidden"} md:block pb-4 md:pb-0 space-y-1`}
      >
        {col.links.map((l) => (
          <li key={(l.to || "contact") + l.tKey}>
            {l.contact ? (
              <button
                type="button"
                onClick={onContact}
                className="min-h-[44px] inline-flex items-center gap-1.5 text-sm text-stone-800 hover:text-[#a85837]"
              >
                <Mail size={14} aria-hidden />
                {t(l.tKey)}
              </button>
            ) : l.external ? (
              <a
                href={l.to}
                target={l.to.startsWith("mailto:") ? undefined : "_blank"}
                rel={l.to.startsWith("mailto:") ? undefined : "noreferrer"}
                className="min-h-[44px] inline-flex items-center gap-1.5 text-sm text-stone-800 hover:text-[#a85837]"
              >
                <Mail size={14} aria-hidden />
                {t(l.tKey)}
              </a>
            ) : (
              <Link
                to={l.to}
                className="min-h-[44px] inline-flex items-center text-sm text-stone-800 hover:text-[#a85837]"
              >
                {t(l.tKey)}
              </Link>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function FooterTerritorialeCompleto() {
  const { t } = useLang();
  const [nlEmail, setNlEmail] = useState("");
  const [nlDone, setNlDone] = useState(false);
  const [nlOffline, setNlOffline] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);

  const submitNl = async (e) => {
    e.preventDefault();
    const email = nlEmail.trim();
    if (!email) return;
    try {
      await subscribeNewsletter(email);
      setNlDone(true);
      setNlOffline(false);
    } catch {
      try {
        localStorage.setItem("6amico_cartolina_domenica_draft", email);
      } catch { /* noop */ }
      setNlDone(true);
      setNlOffline(true);
    }
  };

  return (
    <footer
      className="bg-[#f7efe6] border-t border-[#eedfcb] text-stone-800"
      data-testid="footer-territoriale"
    >
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-10 md:py-14">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-8">
          <div className="md:col-span-1">
            <div className="flex items-center gap-2.5 mb-4">
              <span
                className="w-11 h-11 rounded-full bg-[#c26d4a] text-white font-serif font-bold text-lg flex items-center justify-center border-2 border-[#a85837]"
                aria-hidden
              >
                6a
              </span>
              <span className="font-serif font-bold text-xl text-stone-950">6AMICO</span>
            </div>
            <p className="text-sm text-stone-800 leading-relaxed">
              {t("footx.blurb")}
            </p>
            <div className="mt-4 flex items-center gap-3" aria-label="Social ufficiali">
              <a
                href="https://www.facebook.com/aggiungimiaipreferiti/?locale=it_IT"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Pagina Facebook ufficiale aggiungimiaipreferiti"
                data-testid="footer-facebook-aggiungimi"
                className="inline-flex items-center gap-2 rounded-full bg-[#162d4a] hover:bg-[#0f1f33] text-white px-4 py-2 text-xs font-semibold transition-colors"
              >
                <Facebook size={14} aria-hidden />
                aggiungimiaipreferiti
              </a>
            </div>
            <Link
              to="/censisci"
              className="mt-4 inline-flex items-center justify-center min-h-[48px] px-5 rounded-full bg-stone-900 text-white font-sans font-semibold text-sm hover:bg-stone-700 transition-colors"
              data-testid="footer-cta-censisci"
            >
              {t("footx.cta_censisci")}
            </Link>
            <button
              type="button"
              onClick={() => setContactOpen(true)}
              className="mt-4 inline-flex items-center gap-2 rounded-full border border-[#ecd9c2] bg-[#fdfbf7] px-4 py-2 text-sm font-sans font-semibold text-stone-800 hover:border-[#c26d4a] hover:text-[#a85837] transition-colors break-all"
              data-testid="footer-mail-info"
              aria-label="Scrivici: info@6amico.com"
            >
              <Mail size={14} className="text-[#a85837] shrink-0" aria-hidden />
              info@6amico.com
            </button>
            <form onSubmit={submitNl} className="mt-5">
              <label
                htmlFor="footer-nl"
                className="block text-xs font-sans font-bold uppercase tracking-widest text-stone-800 mb-2"
              >
                {t("footx.nl_title")}
              </label>
              <div className="flex gap-2">
                <input
                  id="footer-nl"
                  type="email"
                  value={nlEmail}
                  onChange={(e) => {
                    setNlEmail(e.target.value);
                    setNlDone(false);
                    setNlOffline(false);
                  }}
                  placeholder={t("footx.nl_placeholder")}
                  className="flex-1 min-h-[48px] rounded-full border border-[#eedfcb] bg-white px-4 text-sm text-stone-950"
                />
                <button
                  type="submit"
                  className="min-h-[48px] min-w-[48px] rounded-full bg-[#c26d4a] hover:bg-[#a85837] text-white flex items-center justify-center"
                  aria-label={t("footx.nl_aria")}
                >
                  <Send size={17} />
                </button>
              </div>
              {nlDone && (
                <p className="mt-2 text-xs font-sans font-semibold text-[#a85837]" role="status">
                  {nlOffline
                    ? t("footx.nl_offline")
                    : t("footx.nl_ok")}
                </p>
              )}
            </form>
          </div>

          <div className="md:col-span-4 grid grid-cols-1 md:grid-cols-4 gap-2 md:gap-6">
            {COLUMNS.map((c) => (
              <FooterColumn key={c.id} col={c} onContact={() => setContactOpen(true)} t={t} />
            ))}
          </div>
        </div>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4">
          {INFO_CARDS.map((card) => (
            <Link
              key={card.testid}
              to={card.to}
              data-testid={card.testid}
              className="rounded-2xl border border-[#ecd9c2] bg-[#fdfbf7] p-5 flex gap-4 hover:border-[#c26d4a] hover:shadow-md transition-all"
            >
              <span className="w-11 h-11 rounded-full bg-[#c26d4a]/15 text-[#a85837] flex items-center justify-center shrink-0" aria-hidden>
                <card.icon size={20} />
              </span>
              <span>
                <span className="block font-serif font-bold text-stone-950">{t(card.tTitle)}</span>
                <span className="block text-sm text-stone-800 mt-1">{t(card.tText)}</span>
              </span>
            </Link>
          ))}
        </div>
      </div>

      <div className="border-t border-[#eedfcb] bg-[#fdfbf7]">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-4 flex flex-col md:flex-row items-center gap-4 md:justify-between">
          <LanguageSwitch compact />
          <p className="text-xs font-sans text-stone-800 text-center order-last md:order-none">
            © 2013-2026 6amico.com · Fondatore: Paolo Pinti
          </p>
          <nav className="flex flex-wrap items-center justify-center gap-4" aria-label="Link legali">
            <button
              type="button"
              onClick={() => setContactOpen(true)}
              className="min-h-[44px] inline-flex items-center gap-1.5 text-xs font-sans font-semibold text-stone-800 hover:text-[#a85837]"
            >
              <Mail size={12} className="text-[#a85837]" aria-hidden />
              info@6amico.com
            </button>
            {LEGAL.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className="min-h-[44px] inline-flex items-center text-xs font-sans font-semibold text-stone-800 hover:text-[#a85837]"
              >
                {t(l.tKey)}
              </Link>
            ))}
            <span className="inline-flex items-center gap-1.5 px-3 min-h-[32px] rounded-full border border-[#ecd9c2] bg-[#f7efe6] text-[10px] font-sans font-bold uppercase tracking-wider text-stone-800">
              <ShieldCheck size={12} className="text-[#a85837]" aria-hidden />
              DSA &amp; AI Act Conforme
            </span>
          </nav>
        </div>
      </div>
      <ProtocolloContattoModal
        open={contactOpen}
        onClose={() => setContactOpen(false)}
        oggettoPreset="Richiesta informazioni — 6amico.com"
      />
    </footer>
  );
}
